import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { MessageCircle, MapPin, Phone, Package, RefreshCcw, Truck } from "lucide-react";
import ordersApi, {
  OrderStatus,
  StoreOrder,
} from "../../api/orders";

const STATUS_TABS: { key: OrderStatus | "ALL"; label: string }[] = [
  { key: "ALL", label: "Toutes" },
  { key: "NEW", label: "Nouvelles" },
  { key: "CONTACTED", label: "Contactées" },
  { key: "CONFIRMED", label: "Confirmées" },
  { key: "PREPARING", label: "En préparation" },
  { key: "SHIPPED", label: "Expédiées" },
  { key: "DELIVERED", label: "Livrées" },
  { key: "CANCELLED", label: "Annulées" },
];

const STATUS_TONE: Record<OrderStatus, string> = {
  NEW: "bg-sky-50 text-sky-700 border-sky-200",
  CONTACTED: "bg-amber-50 text-amber-700 border-amber-200",
  CONFIRMED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  PREPARING: "bg-violet-50 text-violet-700 border-violet-200",
  SHIPPED: "bg-cyan-50 text-cyan-700 border-cyan-200",
  DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CANCELLED: "bg-slate-100 text-slate-600 border-slate-200",
  LOST: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATUS_FLOW: OrderStatus[] = [
  "NEW",
  "CONTACTED",
  "CONFIRMED",
  "PREPARING",
  "SHIPPED",
  "DELIVERED",
];

const money = (value: number) => Number(value || 0).toLocaleString("fr-FR") + " FCFA";

function StatusPill({ status }: { status: OrderStatus }) {
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STATUS_TONE[status]}`}>
      {status}
    </span>
  );
}

/**
 * Public storefront orders.
 *
 * Everything a buyer submits on the marketing site (gestockweb) lands here with
 * their name, phone, quantity, delivery address and notes — plus WhatsApp
 * intents, so the merchant can reconcile the two channels.
 */
export default function Orders() {
  const userdata = useSelector((state: any) => state.UserReducer.userdata);
  const storeId = userdata?.store?._id;

  const [orders, setOrders] = useState<StoreOrder[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<OrderStatus | "ALL">("ALL");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(
    async (silent = false) => {
      if (!storeId) return;
      silent ? setRefreshing(true) : setLoading(true);
      setError("");
      try {
        const data = await ordersApi.list({
          storeId,
          status: tab === "ALL" ? undefined : tab,
          page,
          limit: 25,
        });
        setOrders(data.orders || []);
        setSummary(data.summary || null);
      } catch (e: any) {
        setError(e?.message || "Impossible de charger les commandes.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [storeId, tab, page]
  );

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => orders.find((order) => order._id === selectedId) || null,
    [orders, selectedId]
  );

  const advance = async (order: StoreOrder) => {
    const index = STATUS_FLOW.indexOf(order.status);
    const next = STATUS_FLOW[index + 1];
    if (!next) return;
    try {
      await ordersApi.updateStatus(order._id, { status: next }, storeId);
      toast.success(`Commande passée à « ${next} »`);
      load(true);
    } catch (e: any) {
      toast.error(e?.message || "Mise à jour impossible");
    }
  };

  const setStatus = async (order: StoreOrder, status: OrderStatus) => {
    try {
      await ordersApi.updateStatus(order._id, { status }, storeId);
      toast.success(`Commande « ${status} »`);
      load(true);
    } catch (e: any) {
      toast.error(e?.message || "Mise à jour impossible");
    }
  };

  if (!storeId) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <p className="text-sm text-slate-600">Aucune boutique active.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Commandes en ligne</h1>
            <p className="mt-1 text-sm text-slate-500">
              Toutes les commandes reçues depuis votre boutique en ligne, WhatsApp compris.
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCcw size={15} className={refreshing ? "animate-spin" : undefined} />
            Rafraîchir
          </button>
        </div>

        {/* KPIs */}
        {summary ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {[
              { label: "Total", value: summary.total },
              { label: "Nouvelles", value: summary.new },
              { label: "Confirmées", value: summary.confirmed },
              { label: "Livrées", value: summary.delivered },
              { label: "Chiffre d'affaires", value: money(summary.revenueFcfa) },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* status tabs */}
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                setTab(item.key);
                setPage(1);
              }}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                tab === item.key
                  ? "border-cyan-400 bg-cyan-50 text-cyan-800"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Chargement des commandes…
          </div>
        ) : orders.length === 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
            <p className="text-base font-semibold text-slate-800">Aucune commande pour l'instant</p>
            <p className="mt-1 text-sm text-slate-500">
              Partagez le lien de votre boutique pour recevoir vos premières commandes.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-3">
            {/* list */}
            <div className="space-y-3 lg:col-span-2">
              {orders.map((order) => (
                <button
                  key={order._id}
                  type="button"
                  onClick={() => setSelectedId(order._id)}
                  className={`w-full rounded-2xl border p-4 text-left shadow-sm transition ${
                    selectedId === order._id
                      ? "border-cyan-400 bg-cyan-50/40 ring-2 ring-cyan-200"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-slate-900">
                        {order.reference}
                      </span>
                      <StatusPill status={order.status} />
                      {order.source === "WHATSAPP" ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold uppercase text-emerald-700">
                          WhatsApp
                        </span>
                      ) : null}
                    </div>
                    <span className="text-sm font-semibold text-slate-900">
                      {money(order.totalFcfa)}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">
                      {order.customerName || "Client non renseigné"}
                    </span>
                    {order.customerPhone ? <span>· {order.customerPhone}</span> : null}
                    <span>· {new Date(order.createdAt).toLocaleDateString("fr-FR")}</span>
                    {order.deliveryMode === "DELIVERY" ? (
                      <span className="inline-flex items-center gap-1 text-cyan-700">
                        <Truck size={12} /> Livraison
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-1 text-xs text-slate-500">
                    {(order.lines || [])
                      .map((line) => `${line.productName} ×${line.quantity}`)
                      .join(" , ")}
                  </div>
                </button>
              ))}
            </div>

            {/* detail */}
            <div className="lg:col-span-1">
              {selected ? (
                <div className="sticky top-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-mono text-sm font-semibold text-slate-900">
                      {selected.reference}
                    </p>
                    <StatusPill status={selected.status} />
                  </div>

                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                        <Phone size={12} /> Client
                      </p>
                      <p className="mt-0.5 font-medium text-slate-800">
                        {selected.customerName || "—"}
                      </p>
                      <p className="text-slate-600">{selected.customerPhone || "—"}</p>
                    </div>

                    {selected.deliveryMode === "DELIVERY" ? (
                      <div>
                        <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                          <MapPin size={12} /> Adresse
                        </p>
                        <p className="mt-0.5 text-slate-700">
                          {selected.deliveryAddress || "—"}
                          {selected.deliveryCity ? `, ${selected.deliveryCity}` : ""}
                        </p>
                        {selected.deliveryNotes ? (
                          <p className="mt-0.5 text-xs italic text-slate-500">
                            {selected.deliveryNotes}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    <div>
                      <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                        <Package size={12} /> Articles
                      </p>
                      <ul className="mt-1 space-y-1">
                        {(selected.lines || []).map((line, index) => (
                          <li key={index} className="flex justify-between gap-2 text-slate-700">
                            <span>
                              {line.productName} ×{line.quantity}
                            </span>
                            <span className="whitespace-nowrap text-slate-500">
                              {money(line.lineTotalFcfa)}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 font-semibold text-slate-900">
                        <span>Total</span>
                        <span>{money(selected.totalFcfa)}</span>
                      </div>
                    </div>

                    {selected.customerNotes ? (
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Message client</p>
                        <p className="mt-0.5 rounded-xl bg-slate-50 px-3 py-2 text-slate-700">
                          {selected.customerNotes}
                        </p>
                      </div>
                    ) : null}

                    {selected.source === "WHATSAPP" ? (
                      <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                        Ce client a cliqué sur le bouton WhatsApp. Ses coordonnées sont dans
                        votre conversation WhatsApp.
                      </p>
                    ) : null}

                    {/* actions */}
                    <div className="flex flex-wrap gap-2 pt-1">
                      {selected.customerPhone ? (
                        <a
                          href={ordersApi.whatsappLink(selected)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-xl bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-900"
                        >
                          <MessageCircle size={13} />
                          Répondre
                        </a>
                      ) : null}
                      {STATUS_FLOW.indexOf(selected.status) >= 0 ? (
                        <button
                          type="button"
                          onClick={() => advance(selected)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-cyan-300 bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-800 hover:bg-cyan-100"
                        >
                          Avancer
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setStatus(selected, "CANCELLED")}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                  Sélectionnez une commande pour voir le détail.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}